export const BOOKING_STEPS = {
    SHOP: 1,
    ROOM: 2,
    TURN: 3,
    MODALITY: 4,
    EXTRAS: 5,
    SUMMARY: 6,
    PERSONAL_DATA: 7,
} as const;

export type LandingMode = 'free' | 'shop_locked' | 'shop_room_locked';

export function parseLandingFromPath(pathname: string): {
    shopId: number | null;
    roomNum: number | null;
    mode: LandingMode;
} {
    const segment = pathname.split('/').filter(Boolean)[0];
    if (!segment) {
        return { shopId: null, roomNum: null, mode: 'free' };
    }

    const [shopPart, roomPart] = segment.split('-');
    const shopId = Number(shopPart);
    if (Number.isNaN(shopId)) {
        return { shopId: null, roomNum: null, mode: 'free' };
    }

    if (roomPart !== undefined && roomPart !== '') {
        const roomNum = Number(roomPart);
        if (!Number.isNaN(roomNum)) {
            return { shopId, roomNum, mode: 'shop_room_locked' };
        }
    }

    return { shopId, roomNum: null, mode: 'shop_locked' };
}

export function isStepNavigationAllowed(
    step: number,
    landingMode: LandingMode,
    showRoomInStepper: boolean
): boolean {
    if (step === BOOKING_STEPS.SHOP) {
        return landingMode === 'free';
    }
    if (step === BOOKING_STEPS.ROOM) {
        if (!showRoomInStepper) return false;
        return landingMode !== 'shop_room_locked';
    }
    return true;
}

export const STEP_TITLES: Record<number, string> = {
    [BOOKING_STEPS.SHOP]: '¿Dónde quieres celebrar?',
    [BOOKING_STEPS.ROOM]: 'Elige la sala perfecta',
    [BOOKING_STEPS.TURN]: 'Elige fecha y turno',
    [BOOKING_STEPS.MODALITY]: 'Elige la modalidad de celebración',
    [BOOKING_STEPS.EXTRAS]: 'Añade servicios extra',
    [BOOKING_STEPS.SUMMARY]: 'Resumen',
    [BOOKING_STEPS.PERSONAL_DATA]: 'Ya casi está 🎉',
};

export const STEP_SUBTITLES: Record<number, string> = {
    [BOOKING_STEPS.SHOP]: 'Selecciona el centro que mejor se adapte a ti.',
    [BOOKING_STEPS.ROOM]: 'Cada sala dispone de características diferentes para adaptarse a tu celebración.',
    [BOOKING_STEPS.TURN]: '',
    [BOOKING_STEPS.MODALITY]: 'Selecciona la opción que mejor se adapte a vuestra forma de celebrar.',
    [BOOKING_STEPS.EXTRAS]: 'Personaliza tu celebración con los extras que necesites.',
    [BOOKING_STEPS.SUMMARY]: 'Revisa tu reserva',
    [BOOKING_STEPS.PERSONAL_DATA]: 'Solo necesitamos tus datos para confirmar la reserva.',
};

export const STEP_LABELS: Record<number, string> = {
    [BOOKING_STEPS.SHOP]: 'Centro',
    [BOOKING_STEPS.ROOM]: 'Sala',
    [BOOKING_STEPS.TURN]: 'Fecha',
    [BOOKING_STEPS.MODALITY]: 'Modalidad',
    [BOOKING_STEPS.EXTRAS]: 'Extras',
    [BOOKING_STEPS.SUMMARY]: 'Confirmar',
    [BOOKING_STEPS.PERSONAL_DATA]: 'Pago',
};

export function getVisibleSteps(includeSummaryStep = true): number[] {
    // Steps 1–5 are always shown; only summary (6) is viewport-dependent.
    const steps: number[] = [
        BOOKING_STEPS.SHOP,
        BOOKING_STEPS.ROOM,
        BOOKING_STEPS.TURN,
        BOOKING_STEPS.MODALITY,
        BOOKING_STEPS.EXTRAS,
    ];
    if (includeSummaryStep) {
        steps.push(BOOKING_STEPS.SUMMARY);
    }
    steps.push(BOOKING_STEPS.PERSONAL_DATA);
    return steps;
}

export function getNextStep(
    current: number,
    hasMultipleRooms: boolean,
    includeSummaryStep = true
): number {
    if (current === BOOKING_STEPS.SHOP) {
        return hasMultipleRooms ? BOOKING_STEPS.ROOM : BOOKING_STEPS.TURN;
    }
    if (current === BOOKING_STEPS.ROOM) {
        return BOOKING_STEPS.TURN;
    }
    if (current === BOOKING_STEPS.EXTRAS && !includeSummaryStep) {
        return BOOKING_STEPS.PERSONAL_DATA;
    }
    return current + 1;
}

export function getPreviousStep(
    current: number,
    hasMultipleRooms: boolean,
    landingMode: LandingMode,
    includeSummaryStep = true
): number | null {
    if (current === BOOKING_STEPS.TURN) {
        if (landingMode === 'shop_room_locked') {
            return null;
        }
        if (landingMode === 'shop_locked') {
            return hasMultipleRooms ? BOOKING_STEPS.ROOM : null;
        }
        return hasMultipleRooms ? BOOKING_STEPS.ROOM : BOOKING_STEPS.SHOP;
    }
    if (current === BOOKING_STEPS.ROOM) {
        return landingMode === 'free' ? BOOKING_STEPS.SHOP : null;
    }
    if (current === BOOKING_STEPS.PERSONAL_DATA && !includeSummaryStep) {
        return BOOKING_STEPS.EXTRAS;
    }
    if (current <= BOOKING_STEPS.SHOP) {
        return null;
    }
    return current - 1;
}
